import RequestError from './RequestError.js';
import RequestLogData from './RequestLogData.js';
import RequestLogEvent from './RequestLogEvent.js';

/**
 * @param {?string} method - http method
 * @param {string} route - route without the query
 * @param {?object} query - query parameters as key:value object
 * @param {?object} headers - http headers as key:value object
 * @param {?object} body - object that will be processed into a string body; processing can be overridden
 * @param {?string} logSource - widget/module identifier for logging purposes
 * @param {?string} dataSource - optional identifier of the BE data source for logging purposes
 * @param {?boolean} useDefaultHeaders - whether to use default headers or let user specify
 * @param {?function(object): string} requestProcessFunction - function that processes the request body
 * @param {?function(string): object} responseProcessFunction - function that processes the response body
 * @param {?function(?object): boolean} getIsValidResponse - function that validates the processed response body
 * @param {?function(?object): boolean} getIsExpectedErrorResponse - function that decides if error response is expected
 * @param {?boolean} shouldReturnBlob - set true to avoid text processing and read response stream as blob instead; this will bypass logging (see TODO)
 * @param {?boolean} shouldReturnHeaders - set true to return response headers along with response body, object structure will be {response: *, headers: Headers}
 * @param {?(number[])} expectedErrorCodes - list of error codes that are expected as part of some user journey and will be processed
 * @param {?boolean} isNonessential - marks request as nonessential, downgrading its error log severity level from critical to error
 * @param {?boolean} isFatal - marks request as fatal if it fails, upgrading its error log severity level from critical to fatal
 * @param {?boolean} isImportant - marks request as important, upgrading its debug log severity from debug to info
 * @param {?boolean} doNotLog - mark request as one that should not be logged (mainly the log-sending one)
 * @return {Promise<*>}
 */
export default function request({
  method = 'GET',
  route,
  query = null,
  headers = null,
  body = null,
  logSource = 'unknown',
  dataSource = null,
  useDefaultHeaders = true,
  requestProcessFunction = JSON.stringify,
  responseProcessFunction = JSON.parse,
  getIsValidResponse = () => true,
  getIsExpectedErrorResponse = () => false,
  shouldReturnBlob = false,
  shouldReturnHeaders = false,
  expectedErrorCodes = [],
  isNonessential = false,
  isFatal = false,
  isImportant = false,
  doNotLog = false,
}) {
  const hasQuery = query && Object.entries(query).length !== 0;
  const url = hasQuery ? `${route}?${buildQueryString(query)}` : route;
  const defaultHeaders = {
    Accept: 'application/json; charset=utf-8',
    'Content-type': 'application/json; charset=utf-8',
  };
  const headersObject = useDefaultHeaders
    ? new Headers({
        ...defaultHeaders,
        ...headers,
      })
    : new Headers(headers);

  const requestBody = body ? requestProcessFunction(body) : null;
  const options = {
    method,
    headers: headersObject,
    body: requestBody,
    credentials: 'include',
  };
  const requestObject = new Request(url, options);

  return fetch(requestObject)
    .catch(error => {
      // CORS and similar issues that result in "Failed to fetch" error.
      throw new RequestError({
        requestLogData: new RequestLogData({
          request: requestObject,
          requestBody,
          logSource,
          dataSource,
        }),
        stack: error.stack,
        isNonessential,
        isFatal,
      });
    })
    .then(async responseObject => {
      if (shouldReturnBlob && responseObject.ok) {
        // TODO: add special logging for this case
        return responseObject.blob();
      }

      const rawResponseBody = await responseObject.text();
      let processedResponseBody = null;
      let processingError = null;
      try {
        processedResponseBody = rawResponseBody ? responseProcessFunction(rawResponseBody) : null;
      } catch (error) {
        processingError = error;
      }

      const requestLogData = new RequestLogData({
        request: requestObject,
        requestBody,
        response: responseObject,
        responseBody: rawResponseBody, // raw response body for logging purposes
        logSource,
        dataSource,
        doNotLog,
      });

      if (!responseObject.ok || processingError || !getIsValidResponse(processedResponseBody)) {
        throw new RequestError({
          requestLogData,
          responseStatus: responseObject.status,
          responseBody: processedResponseBody, // parsed response body for error handling purposes
          isExpected:
            [401, ...expectedErrorCodes].some(statusCode => statusCode === responseObject.status) ||
            getIsExpectedErrorResponse(processedResponseBody),
          isNonessential,
          isFatal,
          isProcessing: processingError !== null,
        });
      }

      const logEvent = new RequestLogEvent({
        requestLogData,
        isImportant,
      });
      window.dispatchEvent(logEvent);

      if (shouldReturnHeaders) {
        return {
          response: processedResponseBody,
          headers: responseObject.headers,
        };
      }

      return processedResponseBody;
    });
}

/**
 * @param {object} queryParams
 * @return {string}
 */
export function buildQueryString(queryParams) {
  return Object.keys(queryParams)
    .filter(key => queryParams[key] !== null && queryParams[key] !== undefined)
    .map(key => {
      if (Array.isArray(queryParams[key])) {
        // support for sending array in url - same attribute key for multiple values
        const encodedKey = encodeURIComponent(key);
        return queryParams[encodedKey]
          .map(value => `${encodedKey}=${encodeURIComponent(value)}`)
          .join('&');
      }

      return `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`;
    })
    .join('&');
}

export { default as RequestError } from './RequestError.js';
